import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';

export interface BtDevice { name: string; address: string; }
export interface BtPrintLine { text: string; align: 'L' | 'C' | 'R'; bold: boolean; size: 'S' | 'L'; }

const BluetoothPrintPlugin = registerPlugin<{
  getDevices(): Promise<{ devices: BtDevice[] }>;
  printLines(opts: { address: string; lines: BtPrintLine[] }): Promise<void>;
}>('BluetoothPrint');

@Injectable({ providedIn: 'root' })
export class BluetoothPrintService {

  // Shared in-memory BLE state — one connection reused across all components
  private webBtDevice: any = null;
  private webBtWriteCharacteristic: any = null;

  // Shared storage keys — all features remember the same printer
  readonly BT_ADDR_KEY          = 'app_bt_printer_address';
  readonly WEB_BT_DEVICE_ID_KEY = 'app_web_bt_device_id';

  private readonly serviceUuids = [
    '0000ffe0-0000-1000-8000-00805f9b34fb',
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
    '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    '000018f0-0000-1000-8000-00805f9b34fb',
  ];

  private readonly charUuids = [
    '0000ffe1-0000-1000-8000-00805f9b34fb',
    '49535343-1e4d-4bd9-ba61-23c647249616',
    '00002af1-0000-1000-8000-00805f9b34fb',
  ];

  get hasWebBluetooth(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as any).bluetooth;
  }

  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  getStoredAddress(): string | null {
    return localStorage.getItem(this.BT_ADDR_KEY);
  }

  // Global picker state — rendered once in shell, avoids stacking context issues
  showPicker = false;
  private pickerCallback: ((address: string) => void) | null = null;

  openPickerForPrint(onSelected: (address: string) => void): void {
    this.pickerCallback = onSelected;
    this.showPicker = true;
  }

  handlePickerSelected(address: string): void {
    const cb = this.pickerCallback;
    this.pickerCallback = null;
    this.showPicker = false;
    if (cb) cb(address);
  }

  handlePickerClosed(): void {
    this.pickerCallback = null;
    this.showPicker = false;
  }

  get isPaired(): boolean {
    return !!(localStorage.getItem(this.BT_ADDR_KEY) || localStorage.getItem(this.WEB_BT_DEVICE_ID_KEY));
  }

  get pairedLabel(): string {
    if (localStorage.getItem(this.BT_ADDR_KEY))    return 'Classic BT paired ✓';
    if (localStorage.getItem(this.WEB_BT_DEVICE_ID_KEY)) return 'Web BT paired ✓';
    return 'Not paired';
  }

  storeAddress(addr: string): void {
    localStorage.setItem(this.BT_ADDR_KEY, addr);
  }

  resetDevice(): void {
    localStorage.removeItem(this.BT_ADDR_KEY);
    localStorage.removeItem(this.WEB_BT_DEVICE_ID_KEY);
    this.webBtWriteCharacteristic = null;
    this.webBtDevice = null;
  }

  async getClassicDevices(): Promise<BtDevice[]> {
    const res = await BluetoothPrintPlugin.getDevices();
    return res.devices || [];
  }

  async classicPrint(address: string, lines: BtPrintLine[]): Promise<void> {
    await BluetoothPrintPlugin.printLines({ address, lines });
  }

  async webPrint(lines: BtPrintLine[]): Promise<void> {
    if (!window.isSecureContext) throw new Error('Web Bluetooth requires HTTPS or localhost.');
    const payload = this.buildEscPosBytes(lines);
    const characteristic = await this.getOrConnectCharacteristic();
    await this.writeChunks(characteristic, payload);
  }

  private readonly onDisconnected = (): void => {
    this.webBtWriteCharacteristic = null;
  };

  private async getOrConnectCharacteristic(): Promise<any> {
    const existing = this.webBtWriteCharacteristic;
    if (existing?.service?.device?.gatt?.connected) return existing;

    const bt = (navigator as any).bluetooth;
    if (!bt) throw new Error('Bluetooth API unavailable');

    // 1. Silent reconnect to cached in-memory device — no picker shown
    if (this.webBtDevice) {
      try {
        const server = this.webBtDevice.gatt?.connected
          ? this.webBtDevice.gatt
          : await this.webBtDevice.gatt.connect();
        const char = await this.findCharOnServer(server);
        if (char) { this.webBtWriteCharacteristic = char; return char; }
      } catch {
        // Keep webBtDevice alive — transient failure (printer sleeping); next call retries.
        // Only clear it when the user explicitly resets or a new device is picked below.
      }
    }

    // 2. Silent reconnect via Chrome's remembered device list — no picker shown
    // (returns [] in Incognito mode — unavoidable browser limitation)
    const savedId = localStorage.getItem(this.WEB_BT_DEVICE_ID_KEY);
    if (savedId && typeof bt.getDevices === 'function') {
      try {
        const known: any[] = await bt.getDevices();
        const found = known.find((d: any) => d?.id === savedId);
        if (found) {
          const server = found.gatt?.connected ? found.gatt : await found.gatt.connect();
          const char = await this.findCharOnServer(server);
          if (char) {
            this.webBtDevice = found;
            this.webBtWriteCharacteristic = char;
            this.attachDisconnectListener(found);
            return char;
          }
        }
      } catch { /* fall through to picker */ }
    }

    // 3. If in-memory device exists but all reconnect attempts failed, throw instead of
    //    immediately showing the picker — this avoids the picker for transient dropouts.
    if (this.webBtDevice) {
      throw new Error('Printer not reachable. Make sure it is on and in range, then try again.');
    }

    // 4. No known device at all — show browser picker once
    const device = await bt.requestDevice({
      acceptAllDevices: true,
      optionalServices: this.serviceUuids,
    });
    if (!device) throw new Error('No Bluetooth device selected');

    this.webBtDevice = device;
    if (device.id) localStorage.setItem(this.WEB_BT_DEVICE_ID_KEY, String(device.id));
    this.attachDisconnectListener(device);

    const server = device.gatt?.connected ? device.gatt : await device.gatt.connect();
    if (!server) throw new Error('Unable to connect to Bluetooth printer');

    const char = await this.findCharOnServer(server);
    if (char) { this.webBtWriteCharacteristic = char; return char; }

    throw new Error('Compatible printer write characteristic not found');
  }

  private async findCharOnServer(server: any): Promise<any> {
    for (const uuid of this.serviceUuids) {
      try {
        const service = await server.getPrimaryService(uuid);
        const char = await this.findWritableChar(service);
        if (char) return char;
      } catch { /* try next service */ }
    }
    return null;
  }

  private async findWritableChar(service: any): Promise<any> {
    for (const uuid of this.charUuids) {
      try {
        const c = await service.getCharacteristic(uuid);
        if (c?.properties?.write || c?.properties?.writeWithoutResponse) return c;
      } catch { }
    }
    try {
      const all = await service.getCharacteristics();
      return all.find((c: any) => c?.properties?.write || c?.properties?.writeWithoutResponse) || null;
    } catch { }
    return null;
  }

  private attachDisconnectListener(device: any): void {
    try {
      device.removeEventListener('gattserverdisconnected', this.onDisconnected);
      device.addEventListener('gattserverdisconnected', this.onDisconnected);
    } catch { }
  }

  private async writeChunks(characteristic: any, data: Uint8Array): Promise<void> {
    const chunkSize = 180;
    for (let offset = 0; offset < data.length; offset += chunkSize) {
      const slice = data.slice(offset, Math.min(offset + chunkSize, data.length));
      if (characteristic?.writeValueWithoutResponse) {
        await characteristic.writeValueWithoutResponse(slice);
      } else {
        await characteristic.writeValue(slice);
      }
    }
  }

  buildEscPosBytes(lines: BtPrintLine[]): Uint8Array {
    const ESC = 0x1B, GS = 0x1D;
    const out: number[] = [];
    out.push(ESC, 0x40);                                // Init printer
    out.push(ESC, 0x4D, 0x00);                          // Font A
    out.push(GS,  0x28, 0x45, 0x02, 0x00, 0x05, 0x07); // Max print density
    out.push(ESC, 0x47, 0x01);                          // Double-strike
    out.push(ESC, 0x20, 0x00);                          // Zero char spacing
    for (const line of lines) {
      out.push(ESC, 0x61, line.align === 'C' ? 1 : line.align === 'R' ? 2 : 0);
      if (line.size === 'L') {
        out.push(ESC, 0x21, 0x18); out.push(GS, 0x21, 0x01); out.push(ESC, 0x45, 0x01);
      } else if (line.bold) {
        out.push(ESC, 0x21, 0x08); out.push(GS, 0x21, 0x00); out.push(ESC, 0x45, 0x01);
      } else {
        out.push(ESC, 0x21, 0x00); out.push(GS, 0x21, 0x00); out.push(ESC, 0x45, 0x00);
      }
      for (let i = 0; i < line.text.length; i++) {
        const c = line.text.charCodeAt(i);
        out.push(c < 256 ? c : 0x3F);
      }
      out.push(0x0A);
    }
    out.push(0x0A, 0x0A);                           // Minimal feed before cutter
    out.push(GS, 0x56, 0x00);                       // Full cut
    return new Uint8Array(out);
  }
}
